import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import {
  Button,
  ButtonGroup,
  CircularProgress,
  FormControlLabel,
  Menu,
  MenuItem,
  Switch
} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {useState} from 'react'

import {
  AlertName,
  useCancelAlertSubscription,
  useCreateAlertSubscription,
  useGetAlertSubscription,
  QueryKey
} from '../hooks/useCustomAlerts'

export const SubscriptionButton = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [pendingAlert, setPendingAlert] = useState<'daily' | 'onEvent' | 'all' | null>(null)
  const {
    userIsSubscribed,
    hasDailySubscription,
    hasOnEventSubscription,
    handleToggleAll,
    handleToggleDailySubscriptions,
    handleToggleOnEventSubscriptions
  } = useAlertSubscriptions({setPendingAlert})

  const open = Boolean(anchorEl)

  const dailyPending = pendingAlert === 'daily'
  const onEventPending = pendingAlert === 'onEvent'
  const allPending = pendingAlert === 'all'

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <ButtonGroup variant="contained" sx={{height: '48px'}}>
        <Button
          data-test-id="subscription-button"
          onClick={allPending ? undefined : handleToggleAll}
          sx={{
            backgroundColor: (theme) => (userIsSubscribed ? '#29333D' : theme.palette.common.white),
            pointerEvents: allPending ? 'none' : 'auto',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.grey[userIsSubscribed ? '900' : '400']
            }
          }}
        >
          {allPending ? (
            <CircularProgress
              size={20}
              sx={{color: (theme) => (userIsSubscribed ? theme.palette.common.white : '#29333D')}}
            />
          ) : (
            <NotificationsNoneOutlinedIcon sx={{color: userIsSubscribed ? 'white' : 'black'}} />
          )}
        </Button>
        <Button
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            backgroundColor: (theme) => theme.palette.common.white,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.grey['400']
            }
          }}
        >
          <KeyboardArrowDownIcon sx={{color: 'black'}} />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: -10,
          horizontal: 53
        }}
      >
        <MenuItem>
          <FormControlLabel
            control={
              <Switch checked={hasDailySubscription} onChange={handleToggleDailySubscriptions} />
            }
            label={
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                Daily notifications
                {dailyPending && <CircularProgress size={16} />}
              </div>
            }
          />
        </MenuItem>
        <MenuItem>
          <FormControlLabel
            control={
              <Switch
                checked={hasOnEventSubscription}
                onChange={handleToggleOnEventSubscriptions}
              />
            }
            label={
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                Sensor alerts
                {onEventPending && <CircularProgress size={16} />}
              </div>
            }
          />
        </MenuItem>
      </Menu>
    </>
  )
}

const useAlertSubscriptions = ({
  setPendingAlert
}: {
  setPendingAlert: React.Dispatch<React.SetStateAction<'daily' | 'onEvent' | 'all' | null>>
}) => {
  const queryClient = useQueryClient()
  const subscriptions = useGetAlertSubscription()
  const {mutateAsync: subscribe} = useCreateAlertSubscription()
  const {mutateAsync: unsubscribe} = useCancelAlertSubscription()

  const dailyThresholds = subscriptions.data?.find(
    (s) => s.alertName === AlertName.DAILY_THRESHOLDS
  )
  const dailyAnomalies = subscriptions.data?.find((s) => s.alertName === AlertName.DAILY_ANOMALIES)
  const onEventThresholds = subscriptions.data?.find(
    (s) => s.alertName === AlertName.ONEVENT_THRESHOLDS
  )
  const onEventAnomalies = subscriptions.data?.find(
    (s) => s.alertName === AlertName.ONEVENT_ANOMALIES
  )

  const hasDailySubscription = dailyThresholds !== undefined && dailyAnomalies !== undefined
  const hasOnEventSubscription = onEventThresholds !== undefined && onEventAnomalies !== undefined

  const userIsSubscribed = hasDailySubscription || hasOnEventSubscription

  const subscribeDaily = (): Promise<void>[] => {
    return [
      dailyThresholds?.id ? undefined : subscribe(AlertName.DAILY_THRESHOLDS),
      dailyAnomalies?.id ? undefined : subscribe(AlertName.DAILY_ANOMALIES)
    ].filter((p): p is Promise<void> => p !== undefined)
  }

  const subscribeOnEvent = (): Promise<void>[] => {
    return [
      onEventThresholds?.id ? undefined : subscribe(AlertName.ONEVENT_THRESHOLDS),
      onEventAnomalies?.id ? undefined : subscribe(AlertName.ONEVENT_ANOMALIES)
    ].filter((p): p is Promise<void> => p !== undefined)
  }

  const subscribeAll = (): Promise<void>[] => {
    return [...subscribeDaily(), ...subscribeOnEvent()]
  }

  const unsubscribeDaily = (): Promise<void>[] => {
    return [
      dailyThresholds?.id ? unsubscribe(dailyThresholds.id) : undefined,
      dailyAnomalies?.id ? unsubscribe(dailyAnomalies.id) : undefined
    ].filter((p): p is Promise<void> => p !== undefined)
  }

  const unsubscribeOnEvent = (): Promise<void>[] => {
    return [
      onEventThresholds?.id ? unsubscribe(onEventThresholds.id) : undefined,
      onEventAnomalies?.id ? unsubscribe(onEventAnomalies.id) : undefined
    ].filter((p): p is Promise<void> => p !== undefined)
  }

  const unsubscribeAll = (): Promise<void>[] => {
    return [...unsubscribeDaily(), ...unsubscribeOnEvent()]
  }

  const handleToggleAll = async () => {
    setPendingAlert('all')
    try {
      if (userIsSubscribed) {
        await Promise.all(unsubscribeAll())
      } else {
        await Promise.all(subscribeAll())
      }
    } catch (error) {
      console.error('Error toggling all subscriptions:', error)
    }

    await queryClient.invalidateQueries({queryKey: [QueryKey]})
    setPendingAlert(null)
  }

  const handleToggleDailySubscriptions = async () => {
    setPendingAlert('daily')
    try {
      if (hasDailySubscription) {
        await Promise.all(unsubscribeDaily())
      } else {
        await Promise.all(subscribeDaily())
      }
    } catch (error) {
      console.error('Error toggling daily subscriptions:', error)
    }
    await queryClient.invalidateQueries({queryKey: [QueryKey]})
    setPendingAlert(null)
  }

  const handleToggleOnEventSubscriptions = async () => {
    setPendingAlert('onEvent')
    try {
      if (hasOnEventSubscription) {
        await Promise.all(unsubscribeOnEvent())
      } else {
        await Promise.all(subscribeOnEvent())
      }
    } catch (error) {
      console.error('Error toggling on-event subscriptions:', error)
    }
    await queryClient.invalidateQueries({queryKey: [QueryKey]})
    setPendingAlert(null)
  }

  return {
    userIsSubscribed,
    hasDailySubscription,
    hasOnEventSubscription,
    handleToggleAll,
    handleToggleDailySubscriptions,
    handleToggleOnEventSubscriptions
  }
}
